import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Text,
  Button,
  Group,
  SimpleGrid,
  Card,
  Image,
  Badge,
  Avatar,
  Stack,
  LoadingOverlay,
  Title,
  Box,
} from "@mantine/core";
import { IconPlus, IconBuildingStore } from "@tabler/icons-react";
import CreateShopModal from "./components/CreateShopModal";
import { API_BASE_URL } from "../../config/baseUrls";

const BASE_URL = `${API_BASE_URL}/shop-owner`;
const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
  "Content-Type": "application/json",
});

export default function ShopList() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const fetchShops = async () => {
    setLoading(true);
    try {
      // Changed from /shop to /shops (plural list)
      const res = await fetch(`${BASE_URL}/shops`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) {
        setShops(data.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2} className="ota-gradient-text">
            My Shops
          </Title>
          <Text c="dimmed">Select a shop to manage inventory and settings</Text>
        </div>
        <Button
          leftSection={<IconPlus size={18} />}
          onClick={() => setCreateOpen(true)}
          className="btn-ota-primary"
        >
          New Shop
        </Button>
      </Group>

      <Box pos="relative" mih={200}>
        <LoadingOverlay visible={loading} />

        {shops.length === 0 && !loading ? (
          <Paper p="xl" withBorder ta="center" bg="gray.0">
            <IconBuildingStore
              size={48}
              color="gray"
              style={{ opacity: 0.5 }}
            />
            <Text mt="md" fw={500}>
              You haven't created any shops yet.
            </Text>
            <Text size="sm" c="dimmed">
              Click "New Shop" to get started.
            </Text>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {shops.map((shop) => (
              <Card
                key={shop.id}
                padding="0"
                radius="md"
                withBorder
                className="hover:shadow-md transition cursor-pointer"
                onClick={() => navigate(`/shop-owner/shops/${shop.id}`)}
              >
                {/* Banner Section */}
                <div
                  style={{
                    height: 120,
                    backgroundColor: "#f1f3f5",
                    position: "relative",
                  }}
                >
                  {shop.bannerImageUrl && (
                    <Image src={shop.bannerImageUrl} h="100%" fit="cover" />
                  )}
                  <Badge
                    style={{ position: "absolute", top: 10, right: 10 }}
                    color={shop.isActive ? "green" : "red"}
                    variant="filled"
                  >
                    {shop.isActive ? "Active" : "Inactive"}
                  </Badge>
                </div>

                {/* Info Section */}
                <div
                  style={{
                    padding: "16px",
                    marginTop: "-30px",
                    position: "relative",
                  }}
                >
                  <Avatar
                    src={shop.logoUrl}
                    size="lg"
                    radius="md"
                    className="border-4 border-white shadow-sm"
                    color="blue"
                  >
                    {shop.name ? shop.name[0] : "S"}
                  </Avatar>
                  <Text fw={700} size="lg" mt="xs" lineClamp={1}>
                    {shop.name}
                  </Text>
                  <Text c="dimmed" size="sm" lineClamp={2} h={40}>
                    {shop.description || "No description provided."}
                  </Text>

                  <Group mt="md" justify="space-between">
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                      Products
                    </Text>
                    <Badge variant="light" color="blue">
                      {shop._count?.products || 0}
                    </Badge>
                  </Group>
                </div>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Box>

      <CreateShopModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={fetchShops}
      />
    </Stack>
  );
}
